import type { PodBrand } from '@/types/pod'

/**
 * The InspectVoice pod — branding layer (browser side).
 *
 * InspectVoice is voice-driven playground inspection software for UK inspectors
 * and councils, published by Autaimate (a trading name of Michael Stevenson).
 * The pod also answers for Playregister, the free council-side register that
 * InspectVoice reports import into.
 *
 * SOURCE OF TRUTH. Every value below is lifted from published material —
 * inspectvoice.co.uk (read 23 Aug 2026) and playregister.co.uk. Nothing is
 * invented; the same discipline the grounding fence enforces on the corpus
 * applies to the config.
 *
 * PALETTE — SAMPLED, NOT GUESSED. Two palettes exist and they are not the same
 * thing. The APPLICATION ships a dark theme (bg #0C0F14, surface #151920) which
 * a visitor never sees, and the MARKETING PAGE this pod sits on is light: white
 * panels on slate-50, slate-900 text, emerald CTAs. The values below sample the
 * marketing page, because that is the surface the assistant renders against —
 * dressing the pod in the in-app dark theme would make it look like a component
 * that had escaped from somewhere else.
 *
 * The one exception is the accent: #22C55E is the product's own published accent
 * token and is the exact green of the splash screen served by index.html, so it
 * is the first colour a visitor to inspectvoice.co.uk actually sees. Danger is
 * the published very-high risk token #EF4444, which is the right red on a page
 * about risk ratings.
 *
 * CONTACT. support@autaimate.com is the public sales contact on the home page
 * and in its Organization schema. NO PHONE NUMBER IS SET, deliberately —
 * InspectVoice publishes none, and the Cornwall Play Safety mobile belongs to a
 * different business and must not leak across pods. The SMS notify target
 * belongs in workers/pod.ts, never here.
 *
 * ASSISTANT NAME. "Verity" — verification is what both products turn on, and
 * what neither will overclaim: InspectVoice seals a report so a third party can
 * check it, and Playregister refuses to say it verified a signature it cannot.
 * Distinct from Bell, Rafferty, Rezzi, Reva, Piran and Oxy.
 */
export const inspectvoicePod: PodBrand = {
  id: 'inspectvoice',
  brandName: 'InspectVoice',
  productName: 'InspectVoice',
  assistantName: 'Verity',
  tagline: 'Voice-driven playground inspection software for UK inspectors and councils',
  roleLabel: 'enquiry assistant',
  // Playregister is NAMED here rather than given a starter slot. Seventeen of
  // the forty-seven corpus chunks answer for it, and nothing else on the welcome
  // card signalled it existed — the "what happens to the report" starter is the
  // door to those chunks, but a door with no sign on it. The reader most worth
  // reaching is an inspector who has just read the Playregister section on
  // inspectvoice.co.uk and wants to know whether it comes with the subscription,
  // which is the highest-value question on this pod and the one most likely to be
  // answered wrongly. Naming it costs a clause; a starter would cost a slot that
  // is already earning — annual mains is the disqualifier, cost is universal,
  // offline is the inspector's first practical worry. "Free" is carried here
  // rather than left to retrieval, because it is the load-bearing fact and the
  // one the marketing page refuses to let anyone overstate.
  welcomeIntro:
    "Ask me what InspectVoice does, what it costs, or what happens to a report once it's issued — including Playregister, the free register councils keep it in. Anything about your own sites or your own inspection regime needs Michael to look at it, so I'll take your number and get him to call you back.",
  starters: [
    'Does it do annual main inspections?',
    'What does it cost?',
    'Does it work with no mobile signal?',
    'What happens to the report after it’s issued?',
  ],
  inputPrompt: 'Ask Verity a question',
  footerNote:
    "Verity answers from InspectVoice's own published information — anything about your own sites goes straight to Michael.",
  theme: {
    // Sampled from the marketing page on inspectvoice.co.uk.
    '--color-brand-primary': '5 150 105', // emerald-600 #059669
    '--color-brand-primary-hover': '4 120 87', // emerald-700 #047857
    '--color-brand-accent': '34 197 94', // accent token #22C55E (splash screen)
    '--color-surface-base': '248 250 252', // slate-50 #F8FAFC
    '--color-surface-raised': '255 255 255', // white #FFFFFF
    '--color-surface-subtle': '241 245 249', // slate-100 #F1F5F9
    '--color-text-primary': '15 23 42', // slate-900 #0F172A
    '--color-text-muted': '71 85 105', // slate-600 #475569
    '--color-text-danger': '239 68 68', // risk very-high #EF4444
    '--color-border-subtle': '226 232 240', // slate-200 #E2E8F0
  },
  // Opt in to the "Built by Foredesk" credit under the composer. This is
  // Michael's own product and Michael's own decision, as with the playsafety
  // pod; it stays off for pods belonging to other people unless they ask.
  builtByForedesk: true,
  // EMPTIED 23 Aug 2026, on embedding — this is the move the previous version of
  // this comment said to make, carried out. The address was set while the pod ran
  // standalone on chat.inspectvoice.co.uk, where nothing around the frame carried
  // a contact route. Inside inspectvoice.co.uk it does: support@autaimate.com is
  // already in that page's footer and its CTA, so repeating it a third time inside
  // a small frame adds noise without adding a route. Same reasoning as the
  // playsafety pod, which is embedded and empty for the same reason.
  //
  // Verity still gives the address in conversation where it is genuinely the
  // answer, and the SMS/callback path is untouched — this is presentation only.
  //
  // If this pod is ever un-embedded and served on its own URL again, populate it
  // back: contact: { email: 'support@autaimate.com' }. No phone in either case —
  // InspectVoice publishes none, and borrowing the Cornwall Play Safety number
  // would put an unpublished route in front of a visitor.
  contact: {},
}
